import { use, useEffect, useReducer } from 'react';

import { request } from '@portal/pkg/api';
import type {
	Capability,
	Collection,
	Key,
	KeyEvent,
} from '@portal/pkg/api/types';

/**
 *	In-flight and resolved responses, keyed by what they are.
 *
 *	`use()` needs the same promise across renders — a fresh one each render
 *	suspends forever — so it cannot come from `useState` or a ref initializer,
 *	both of which React's development double-render would call twice. A module
 *	map makes the second render a hit, and one request.
 */
const cache: Map<string, Promise<unknown>> = new Map();

const listeners: Set<() => void> = new Set();

const KeysKey = 'keys';
const CapabilitiesKey = 'keys:capabilities';

const eventsKey = (id: string): string => `keys:${id}:events`;

function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
	const existing = cache.get(key) as Optional<Promise<T>>;

	if (existing) {
		return existing;
	}

	const pending = load();

	cache.set(key, pending);

	// A failure must not stick, or the view can never retry. Guarded on
	// identity so a rejection landing after an invalidation cannot evict the
	// replacement that already took its place.
	pending.catch(() => {
		if (cache.get(key) === pending) {
			cache.delete(key);
		}
	});

	return pending;
}

function notify(): void {
	for (const listener of listeners) {
		listener();
	}
}

/** Re-renders a suspending hook when what it read has been dropped. */
function useCacheRevision(): void {
	const [, bump] = useReducer((revision: number) => revision + 1, 0);

	useEffect(() => {
		listeners.add(bump);

		return () => {
			listeners.delete(bump);
		};
	}, []);
}

/**
 *	After a mutation. The key list and any history changed; the catalogue of
 *	grantable capabilities did not.
 *
 *	A refetch rather than a merge: rotating with `revokeCurrent` changes the
 *	predecessor too, and rebuilding that here would duplicate the server's
 *	rules and get `status` wrong.
 */
export function invalidateKeys(): void {
	cache.delete(KeysKey);

	for (const key of [...cache.keys()]) {
		if (key.endsWith(':events')) {
			cache.delete(key);
		}
	}

	notify();
}

/** On any token change — signing in, switching organization, signing out. */
export function resetKeys(): void {
	cache.clear();
	notify();
}

/** Suspends until the collection is there, and again if what it read is dropped. */
function useCollection<T>(cacheKey: string, path: string): T[] {
	useCacheRevision();

	return use(cached(cacheKey, () => request<Collection<T>>('GET', path)))
		.records;
}

export function useKeys(): Key[] {
	return useCollection<Key>(KeysKey, '/api/keys');
}

export function useCapabilities(): Capability[] {
	return useCollection<Capability>(CapabilitiesKey, '/api/keys/capabilities');
}

export function useKeyEvents(id: string): KeyEvent[] {
	return useCollection<KeyEvent>(eventsKey(id), `/api/keys/${id}/events`);
}

export interface CreateKeyPayload {
	name: string;
	/** The public half. The private one never leaves the browser. */
	publicKey: string;
	capabilities: string[];
	/** Unix seconds. Zero asks for the longest life the API allows. */
	expiresAt: number;
}

export interface RotateKeyPayload {
	publicKey: string;
	expiresAt: number;
	/** Ends the predecessor now instead of leaving it the grace window. */
	revokeCurrent: boolean;
}

export interface KeyActions {
	createKey: (payload: CreateKeyPayload) => Promise<Key>;
	rotateKey: (id: string, payload: RotateKeyPayload) => Promise<Key>;
	/** `includeReplaced` ends every generation this key replaced as well. */
	revokeKey: (id: string, includeReplaced?: boolean) => Promise<Key>;
	updateCapabilities: (id: string, capabilities: string[]) => Promise<Key>;
}

/**
 *	The mutations. Not suspending: a form being submitted has to disable its
 *	button, not unmount itself into a fallback.
 *
 *	A constant rather than something built per render: nothing here reads hook
 *	state, and a fresh object each render would churn the identity every effect
 *	and memo downstream depends on.
 */
const actions: KeyActions = {
	async createKey(payload) {
		const key = await request<Key>('POST', '/api/keys', { payload });

		invalidateKeys();

		return key;
	},

	async rotateKey(id, payload) {
		const key = await request<Key>('POST', `/api/keys/${id}/rotate`, {
			payload,
		});

		invalidateKeys();

		return key;
	},

	async revokeKey(id, includeReplaced = false) {
		const key = await request<Key>(
			'POST',
			`/api/keys/${id}/revoke${includeReplaced ? '?includeReplaced=true' : ''}`
		);

		invalidateKeys();

		return key;
	},

	async updateCapabilities(id, capabilities) {
		const key = await request<Key>('PATCH', `/api/keys/${id}`, {
			payload: { capabilities },
		});

		invalidateKeys();

		return key;
	},
};

export function useKeyActions(): KeyActions {
	return actions;
}
